import { useCallback, useEffect, useState } from 'react';
import type { ConversationBooking } from '../../../api/types';
import type { CommsSource } from '../source';

/**
 * Everything one conversation has produced in dispatch, kept current.
 *
 * Loaded once per thread and then patched from `booking.created`, which is what
 * makes the strip work on the side of the desk it was built for: two agents in
 * one inbox, where the whole point is that the SECOND one finds out. A poll or
 * a reload-on-focus would tell them a minute later, by which time the second
 * car is booked.
 *
 * `add` is the local half — the agent who just booked should not wait for their
 * own socket round trip to see their card, and the id is the de-dupe, so an
 * event for a row already added replaces it rather than doubling it.
 *
 * A failed load leaves the strip absent, never broken: it is context, and a
 * thread with no strip is exactly what every thread looked like before this.
 */
export function useThreadBookings(source: CommsSource, conversationDisplayId: number) {
  // The conversation is stored WITH the rows rather than cleared by an effect
  // when it changes: a clear that runs a render late draws the last thread's
  // bookings under this one's header for a frame, which on a strip that exists
  // to say "this is already booked" is the one wrong thing it can say.
  const [state, setState] = useState<{ forConversation: number; rows: ConversationBooking[] }>({
    forConversation: conversationDisplayId,
    rows: [],
  });

  const merge = useCallback((row: ConversationBooking) => {
    setState((current) => {
      // A socket row names its conversation; a locally-added one does not,
      // because the caller already knows which thread it just booked from.
      if (row.conversationId !== undefined && row.conversationId !== current.forConversation) {
        return current;
      }
      const without = current.rows.filter((existing) => existing.id !== row.id);
      // Newest first, by the row id — created_at ties on two saves in one
      // second, and the id is the only total order the service guarantees.
      return { ...current, rows: [row, ...without].sort((a, b) => b.id - a.id) };
    });
  }, []);

  useEffect(() => {
    let live = true;
    source.api
      .listConversationBookings(conversationDisplayId)
      .then((rows) => {
        if (live) setState({ forConversation: conversationDisplayId, rows });
      })
      .catch(() => {
        // Context, not content. Nothing is shown and nothing is said.
      });
    return () => {
      live = false;
    };
  }, [source, conversationDisplayId]);

  useEffect(
    () =>
      source.subscribe((event) => {
        if (event.name !== 'booking.created') return;
        const row = event.payload as ConversationBooking;
        // The event carries the conversation it belongs to precisely because a
        // socket reaches every thread this agent can see, not only the open one.
        if (row?.conversationId !== conversationDisplayId) return;
        merge(row);
      }),
    [source, conversationDisplayId, merge],
  );

  // A load still in flight for THIS thread shows nothing rather than the last
  // one's rows — see the note on the state shape above.
  const bookings = state.forConversation === conversationDisplayId ? state.rows : [];
  return { bookings, add: merge };
}
